import { scheduleCableReconnectWhenIdle } from "@/lib/cable";

const ACCESS_TOKEN_KEY = "c456.access_token";
const REFRESH_TOKEN_KEY = "c456.refresh_token";
const ACCESS_EXPIRES_AT_KEY = "c456.access_expires_at";
const PROACTIVE_REFRESH_MS = 90_000;
const SCHEDULER_INTERVAL_MS = 30_000;

type TokenPair = {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
};

let refreshPromise: Promise<boolean> | null = null;
let refreshSchedulerId: ReturnType<typeof setInterval> | null = null;
let visibilityListenerAttached = false;
/** logout/clearTokens 递增；进行中的 refresh 仅在 epoch 未变时写回 token */
let authEpoch = 0;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly payload: unknown,
  ) {
    super(extractErrorMessage(payload) ?? `Request failed with status ${status}`);
    this.name = "ApiError";
  }

  get code(): string | undefined {
    return extractErrorCode(this.payload);
  }
}

function extractErrorMessage(payload: unknown): string | undefined {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "error" in payload &&
    typeof payload.error === "object" &&
    payload.error !== null &&
    "message" in payload.error &&
    typeof payload.error.message === "string"
  ) {
    return payload.error.message;
  }

  return undefined;
}

function extractErrorCode(payload: unknown): string | undefined {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "error" in payload &&
    typeof payload.error === "object" &&
    payload.error !== null &&
    "code" in payload.error &&
    typeof payload.error.code === "string"
  ) {
    return payload.error.code;
  }

  return undefined;
}

function parseResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  return contentType.includes("application/json") ? response.json() : response.text();
}

function decodeJwtExpMs(accessToken: string): number | null {
  try {
    const payloadPart = accessToken.split(".")[1];
    if (!payloadPart) return null;

    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function persistAccessExpiresAt(accessToken: string, expiresIn?: number) {
  const expiresAt =
    typeof expiresIn === "number"
      ? Date.now() + expiresIn * 1000
      : decodeJwtExpMs(accessToken);

  if (expiresAt !== null) {
    window.localStorage.setItem(ACCESS_EXPIRES_AT_KEY, String(expiresAt));
  }
}

function getAccessExpiresAt(): number | null {
  const raw = window.localStorage.getItem(ACCESS_EXPIRES_AT_KEY);
  if (!raw) return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/** 旧会话仅有 access_token、无 expires_at 时从 JWT exp 补全，以便主动续签 */
function backfillAccessExpiresAtIfMissing() {
  const accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken || getAccessExpiresAt() !== null) return;

  const expMs = decodeJwtExpMs(accessToken);
  if (expMs !== null) {
    window.localStorage.setItem(ACCESS_EXPIRES_AT_KEY, String(expMs));
  }
}

function shouldProactivelyRefresh(): boolean {
  const expiresAt = getAccessExpiresAt();
  if (!expiresAt) return false;

  return expiresAt - Date.now() <= PROACTIVE_REFRESH_MS;
}

function redirectToLogin() {
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  const epochAtStart = authEpoch;

  refreshPromise = (async () => {
    try {
      const refreshToken = api.getRefreshToken();
      if (!refreshToken) return false;

      const response = await window.fetch("/api/v1/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) return false;

      const payload = (await parseResponse(response)) as Partial<TokenPair>;
      if (!payload.access_token || !payload.refresh_token) return false;

      if (epochAtStart !== authEpoch) return false;

      api.setTokens(payload as TokenPair);
      scheduleCableReconnectWhenIdle();
      return true;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function maybeProactiveRefresh() {
  if (!api.getAccessToken()) return;
  if (shouldProactivelyRefresh()) {
    void refreshAccessToken();
  }
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    maybeProactiveRefresh();
  }
}

function ensureVisibilityListener() {
  if (visibilityListenerAttached) return;

  document.addEventListener("visibilitychange", handleVisibilityChange);
  visibilityListenerAttached = true;
}

function removeVisibilityListener() {
  if (!visibilityListenerAttached) return;

  document.removeEventListener("visibilitychange", handleVisibilityChange);
  visibilityListenerAttached = false;
}

function resolveApiUrl(path: string) {
  return path.startsWith("/api/") ? path : `/api/v1${path}`;
}

async function request<T>(path: string, options: RequestInit = {}, retried = false): Promise<T> {
  const headers = new Headers(options.headers);
  const accessToken = api.getAccessToken();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  // 字符串 body 默认按 JSON 发，避免漏设 Content-Type 时 Rails 读不到 params
  if (typeof options.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await window.fetch(resolveApiUrl(path), { ...options, headers });
  const payload = await parseResponse(response);

  if (response.status === 401 && !retried && await refreshAccessToken()) {
    return request<T>(path, options, true);
  }

  if (response.status === 401) {
    api.clearTokens();
    redirectToLogin();
  }

  if (extractErrorCode(payload) === "account_locked" && window.location.pathname !== "/locked") {
    window.location.assign("/locked");
  }

  if (extractErrorCode(payload) === "membership_disabled" && window.location.pathname.startsWith("/accounts/")) {
    window.localStorage.removeItem("c456.current_account_id");
    window.location.assign("/");
  }

  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

export const api = {
  fetch: request,

  fetchForAccount: <T>(accountId: string | number, path: string, options: RequestInit = {}) =>
    request<T>(path, {
      ...options,
      headers: {
        ...Object.fromEntries(new Headers(options.headers).entries()),
        "X-Account-Id": String(accountId),
      },
    }),

  getAccessToken: () => window.localStorage.getItem(ACCESS_TOKEN_KEY),

  getRefreshToken: () => window.localStorage.getItem(REFRESH_TOKEN_KEY),

  setTokens: ({ access_token, refresh_token, expires_in }: TokenPair) => {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
    window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    persistAccessExpiresAt(access_token, expires_in);
  },

  clearTokens: () => {
    authEpoch += 1;
    refreshPromise = null;
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    window.localStorage.removeItem(ACCESS_EXPIRES_AT_KEY);
    api.stopTokenRefreshScheduler();
  },

  refreshAccessToken,

  startTokenRefreshScheduler: () => {
    api.stopTokenRefreshScheduler();
    backfillAccessExpiresAtIfMissing();
    ensureVisibilityListener();
    maybeProactiveRefresh();
    refreshSchedulerId = setInterval(maybeProactiveRefresh, SCHEDULER_INTERVAL_MS);
  },

  stopTokenRefreshScheduler: () => {
    if (refreshSchedulerId !== null) {
      clearInterval(refreshSchedulerId);
      refreshSchedulerId = null;
    }

    removeVisibilityListener();
  },
};
